import React from "react";

const HTML_Accessibility = () => {
  return (
    <div className="accessibility-container">
      <h1 className="accessibility-title">HTML Accessibility Guide</h1>

      {/* Table of Contents */}
      <div className="toc">
        <h2>Table of Contents</h2>
        <ul>
          <li>
            <a href="#introduction">1. Introduction to Accessibility</a>
          </li>
          <li>
            <a href="#semantic-html">2. Semantic HTML</a>
          </li>
          <li>
            <a href="#screen-readers">3. Screen Reader Support</a>
          </li>
          <li>
            <a href="#aria">4. ARIA Roles and Attributes</a>
          </li>
          <li>
            <a href="#keyboard-navigation">5. Keyboard Navigation</a>
          </li>
          <li>
            <a href="#color-contrast">6. Color & Contrast</a>
          </li>
          <li>
            <a href="#forms-accessibility">7. Forms Accessibility</a>
          </li>
          <li>
            <a href="#media-accessibility">8. Media Accessibility</a>
          </li>
          <li>
            <a href="#best-practices">9. Best Practices</a>
          </li>
        </ul>
      </div>

      {/* Sections */}
      <section id="introduction">
        <h2>1. Introduction to Accessibility</h2>
        <p>
          HTML Accessibility ensures web content is usable by all users,
          including people with disabilities. Accessible websites improve
          usability, compliance with WCAG, and user experience for everyone.
        </p>
      </section>
      <hr />

      <section id="semantic-html">
        <h2>2. Semantic HTML</h2>
        <p>
          Semantic HTML elements give meaning to content and improve
          accessibility:
        </p>
        <ul>
          <li>
            <code>&lt;header&gt;</code>, <code>&lt;nav&gt;</code>,{" "}
            <code>&lt;main&gt;</code>, <code>&lt;footer&gt;</code> – page
            structure
          </li>
          <li>
            <code>&lt;article&gt;</code>, <code>&lt;section&gt;</code>,{" "}
            <code>&lt;aside&gt;</code> – content sections
          </li>
          <li>
            <code>&lt;strong&gt;</code> and <code>&lt;em&gt;</code> – emphasize
            importance
          </li>
          <li>
            <code>&lt;h1&gt; - &lt;h6&gt;</code> – proper heading hierarchy
          </li>
        </ul>
        <p>Semantic HTML helps screen readers understand content structure.</p>
      </section>
      <hr />

      <section id="screen-readers">
        <h2>3. Screen Reader Support</h2>
        <p>
          Screen readers read content aloud for visually impaired users.
          Important practices:
        </p>
        <ul>
          <li>Use proper headings and landmarks</li>
          <li>
            Add <code>alt</code> attributes to images
          </li>
          <li>Provide descriptive text for links</li>
          <li>
            Use <code>&lt;label&gt;</code> for form controls
          </li>
        </ul>
        <p>Example:</p>
        <pre>&lt;img src="logo.png" alt="ETM Academy Logo"&gt;</pre>
      </section>
      <hr />

      <section id="aria">
        <h2>4. ARIA Roles and Attributes</h2>
        <p>
          ARIA (Accessible Rich Internet Applications) enhances accessibility
          for dynamic content:
        </p>
        <ul>
          <li>
            <code>role</code> – define element type (<code>role="button"</code>)
          </li>
          <li>
            <code>aria-label</code> – provide accessible names
          </li>
          <li>
            <code>aria-hidden</code> – hide non-essential content from assistive
            technologies
          </li>
          <li>
            <code>aria-live</code> – notify screen readers of dynamic updates
          </li>
        </ul>
        <p>Example:</p>
        <pre>
          &lt;button role="button" aria-label="Submit
          Form"&gt;Submit&lt;/button&gt;
        </pre>
      </section>
      <hr />

      <section id="keyboard-navigation">
        <h2>5. Keyboard Navigation</h2>
        <p>All interactive elements should be accessible via keyboard:</p>
        <ul>
          <li>
            Use <code>tabindex</code> to control focus order
          </li>
          <li>Ensure buttons, links, and forms are focusable</li>
          <li>Provide visible focus indicators using CSS</li>
        </ul>
        <p>Example:</p>
        <pre>&lt;button tabindex="0"&gt;Click Me&lt;/button&gt;</pre>
      </section>
      <hr />

      <section id="color-contrast">
        <h2>6. Color & Contrast</h2>
        <p>Ensure sufficient contrast for text and backgrounds:</p>
        <ul>
          <li>Use high contrast colors for readability</li>
          <li>Do not rely solely on color to convey information</li>
          <li>Test with contrast checker tools</li>
        </ul>
        <p>Example CSS:</p>
        <pre>
          {`p {
  color: #333; 
  background-color: #fff;
}`}
        </pre>
      </section>
      <hr />

      <section id="forms-accessibility">
        <h2>7. Forms Accessibility</h2>
        <ul>
          <li>
            Use <code>&lt;label&gt;</code> associated with inputs (
            <code>for</code> attribute)
          </li>
          <li>Provide placeholder text and error messages</li>
          <li>
            Group related fields using <code>&lt;fieldset&gt;</code> and{" "}
            <code>&lt;legend&gt;</code>
          </li>
        </ul>
        <p>Example:</p>
        <pre>
          {`<label for="email">Email:</label>
<input type="email" id="email" name="email" required>`}
        </pre>
      </section>
      <hr />

      <section id="media-accessibility">
        <h2>8. Media Accessibility</h2>
        <ul>
          <li>Provide captions or subtitles for videos</li>
          <li>Provide transcripts for audio content</li>
          <li>
            Use <code>&lt;figure&gt;</code> and <code>&lt;figcaption&gt;</code>{" "}
            for images
          </li>
          <li>Ensure autoplay content can be paused or stopped</li>
        </ul>
        <p>Example:</p>
        <pre>
          {`<video controls>
  <source src="video.mp4" type="video/mp4">
  Your browser does not support the video tag.
</video>`}
        </pre>
      </section>
      <hr />

      <section id="best-practices">
        <h2>9. Best Practices</h2>
        <ul>
          <li>Use semantic HTML as the foundation</li>
          <li>Ensure all interactive elements are keyboard-accessible</li>
          <li>Provide alternative text for images and media</li>
          <li>Test accessibility using tools like Lighthouse, WAVE, and axe</li>
          <li>Follow WCAG 2.1 guidelines</li>
          <li>
            Maintain color contrast ratios of at least 4.5:1 for normal text
          </li>
        </ul>
      </section>
      <hr />

      <p className="accessibility-footer">
        HTML Accessibility ensures an inclusive, usable web for everyone.
      </p>
    </div>
  );
};

export default HTML_Accessibility;
